<template>
    <div class="referral-chat-modal">
        <common-modal
            v-if="isOpen"
            width="medium"
            @emitEvent="emitEvent($event, ['close'])"
            @close="emitEvent({ name: 'close' })"
        >
            <div class="referral-chat-modal__common-modal">
                <common-page-header :heading="getMessagingLabel" :isCenter="true" size="small">
                    <template v-if="!isChatWithPatient" #content>
                        <span v-if="referral && referral.shouldShowPatientInfo">
                            Patient name:
                            <common-patient-name :patient="referral.patient"></common-patient-name>
                        </span>
                        <span v-else-if="referral">
                            {{ $t('modals.referral_chat_modal.not_aprooved_patient') }}
                        </span>
                    </template>
                </common-page-header>
            </div>
            <div v-show="showLoadingInfo">
                <p class="referral-chat-modal__loading-message">{{ $t('modals.referral_chat_modal.loading_messages') }}</p>
            </div>
            <common-content-spacer
                v-show="!showLoadingInfo"
                id="container"
                ref="scrollComponent"
                height="tiny"
                class="referral-chat-modal__messages-container"
            >
                <message-row
                    v-for="(message, index) in messages"
                    :key="index"
                    :message="message"
                    :userId="userId"
                    :referral="referral"
                    :showAttachment="true"
                    :isBothPracticesAncillary="isBothPracticesAncillary"
                    :practice="practice"
                ></message-row>
            </common-content-spacer>
            <common-content-spacer height="tiny">
                <div class="referral-chat-modal__text-editor">
                    <text-editor-input
                        :uploadedFiles="selectedFiles"
                        :showUploadFileIcon="true"
                        :value="sendMessageText"
                        :placeholder="$t('modals.referral_chat_modal.text_editor_placeholder', { name: messageRecipient })"
                        @attach-file="attachFile"
                        @change="messageTextChanged"
                        @remove-file="removeFile($event)"
                    ></text-editor-input>
                </div>
                <span>
                    <common-warning v-if="showWarning" size="medium" :text="warningText"></common-warning>
                </span>
                <common-buttons align="right">
                    <common-button
                        icon="send-message"
                        :label="$t('modals.referral_chat_modal.send_message_button')"
                        :isProcessing="isSending"
                        :isDisabled="isDisabled || showWarning"
                        size="small"
                        type="primary"
                        @click="sendMessage()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
            <input
                ref="fileInput"
                type="file"
                multiple="multiple"
                style="display: none"
                accept="application/pdf,image/png,image/jpg,image/jpeg"
                @change="fileSelected($event)"
            />
        </common-modal>
    </div>
</template>

<script>
    import TextEditorInput from 'components/form-inputs/TextEditorInput'
    import MessageRow from 'components/page-elements/referrals/messages/MessageRow'
    import MessagesAPI from 'api/MessagesAPI'
    import UploadMixin from 'mixins/UploadMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import Store from '@/store'

    export default {
        mixins: [UploadMixin],
        components: {
            MessageRow,
            TextEditorInput,
        },
        props: {
            referral: { type: Object, required: true },
            isOpen: { type: Boolean, required: true },
            isBothPracticesAncillary: { type: Boolean, required: true },
            isChatWithPatient: { type: Boolean, default: false },
        },

        data() {
            return {
                userId: null,
                firstLoad: true,
                showLoadingInfo: false,
                isLoading: false,
                totalMessages: 0,
                offset: 0,
                limit: 10,
                messages: [],
                sendMessageText: '',
                messageContainer: null,
                isSending: false,
                PracticeTypeEnum: PracticeTypeEnum,
            }
        },

        computed: {
            getMessagingLabel() {
                if (this.isChatWithPatient) {
                    return this.$t('modals.referral_chat_modal.recipient_patient_header', {
                        name: this.messageRecipient,
                    })
                } else {
                    return this.$t('modals.referral_chat_modal.recipient_header', {
                        name: this.messageRecipient,
                    })
                }
            },
            messageRecipient() {
                if (!this.referral || !this.referral.fromUser) return ''

                if (this.isChatWithPatient) {
                    return this.patientName
                }

                if (this.userId !== this.referral.fromUser.id) {
                    return this.referral.fromUser?.firstName + ' ' + this.referral.fromUser?.lastName
                }

                if (this.referral.toUser) {
                    return this.referral.toUser?.firstName + ' ' + this.referral.toUser?.lastName
                }

                return this.referral.practice.name
            },
            patientName() {
                if (!this.referral) return ''
                return this.referral.patient?.firstName + ' ' + this.referral.patient?.lastName
            },
            patientHeader() {
                if (!this.referral) return ''
                return this.referral.shouldShowPatientInfo
                    ? this.$t('modals.referral_chat_modal.patient_header', { name: this.patientName })
                    : this.$t('modals.referral_chat_modal.not_aprooved_patient')
            },
            isDisabled() {
                return !this.sendMessageText && !this.selectedFiles.length
            },

            filesAlreadyUploaded() {
                return this.selecteFiles.filter((file) => !!file.file.id)
            },
        },

        methods: {
            messageTextChanged(newValue) {
                this.sendMessageText = newValue
            },
            async sendMessage() {
                if ((!this.sendMessageText?.length && !this.selectedFiles.length) || this.isSending) {
                    return
                }

                let NewMessagefromUserId = null
                if (!this.isBothPracticesAncillary && this.practice.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    NewMessagefromUserId = this.userId
                } else if (this.$auth.isUserPracticeOther()) {
                    NewMessagefromUserId = this.$auth.getUser().id
                }

                let NewMessagetoUserId = null
                if (!this.isBothPracticesAncillary) {
                    NewMessagetoUserId =
                        this.userId === this.referral.fromUser.id ? this.referral.toUser?.id ?? null : this.referral.fromUser.id
                }
                const query = {
                    fromUserId: NewMessagefromUserId,
                    toUserId: NewMessagetoUserId,
                    objectId: this.referral.id,
                    objectType: 'referral',
                    body: this.sendMessageText || '',
                    fromPracticeId: this.practice.id,
                    toPracticeId:
                        this.practice?.id === this.referral?.fromPractice?.id
                            ? this.referral.practice.id
                            : this.referral?.fromPractice?.id,
                    properties: `{"referralFromUserId":${
                        !this.isBothPracticesAncillary ? this.referral.fromUser.id : null
                    },"referralToUserId":${this.referral.toUser?.id ?? null}, "fromPracticeId":${
                        this.practice && this.practice.id ? this.practice.id : null
                    },"toPracticeId":${
                        this.practice.id === this.referral?.fromPractice?.id
                            ? this.referral?.practice?.id
                            : this.referral?.fromPractice?.id
                    } }`,
                }

                if (this.isChatWithPatient) {
                    query.toPatientId = this.referral.patient.id
                    delete query.toPracticeId
                    delete query.toUserId
                }

                if (this.selectedFiles.length) {
                    this.uploadScope = this.isChatWithPatient
                        ? 'patient-provider-message-attachment'
                        : 'referral-message-attachment'
                    try {
                        const results = await this.prepareUploads(this.uploadScope)
                        const filesForUpload = this.selectedFiles.filter((file) => !file.isUploaded)
                        query.attachments = filesForUpload.map((item, index) => ({
                            uploadKey: item.uploadKey || results[index]?.uploadKey,
                            fileName: item.file.name,
                            size: Math.round(item.file.size / 1024),
                        }))
                    } catch {
                        this.isSending = false
                        return
                    }
                }
                this.isSending = true
                return MessagesAPI.sendMessage(query)
                    .then(() => {
                        this.checkForNewMessages().then(() => {
                            this.scrollToEnd()
                            this.sendMessageText = ''
                            this.selectedFiles = []
                            this.isSending = false
                        })
                    })
                    .catch(() => {
                        this.isSending = false
                    })
            },
            markSeen() {
                const query = {
                    toUserId: this.userId,
                    objectId: this.referral.id,
                    practiceId: this.practice.id,
                    objectType: 'referral',
                }

                return MessagesAPI.markSeen(query).then(() => this.getUnreadMessages())
            },
            getUnreadMessages() {
                const _fields = ['id']
                const query = {
                    toUserId:
                        this.$auth.getPractice().practiceType === this.PracticeTypeEnum.ANCILLARY
                            ? 0
                            : this.$auth.getActiveMember().id,
                    toPracticeId: this.$auth.getPractice().id,
                }
                MessagesAPI.getUnread(query, _fields).then((resp) => {
                    Store.dispatch('chat-messages/setUnread', resp)
                })
            },
            checkForNewMessages() {
                const query = {
                    objectId: this.referral.id,
                    userId:
                        (this.practice && this.practice.practiceType === PracticeTypeEnum.PHYSICIAN) ||
                        this.$auth.isUserPracticeOther()
                            ? this.userId
                            : 0,
                    objectType: 'referral',
                    limit: this.limit,
                    offset: 0,
                    order: 'desc',
                }

                if (this.isChatWithPatient) {
                    query.patientId = Number(this.referral.patient.id)
                }

                if (this.practice && this.practice?.id) {
                    query.practiceId = this.practice?.id
                } else {
                    query.practiceId = 0
                }

                return MessagesAPI.getMessages(query)
                    .then((resp) => {
                        resp.forEach((message) => {
                            if (
                                this.messages.length === 0 ||
                                message.createdDateTime >= this.messages[this.messages.length - 1].createdDateTime
                            ) {
                                this.messages.push(message)
                            }
                        })
                        this.totalMessages = resp._headers['x-total-count']
                        this.isLoading = false
                    })
                    .catch(() => {
                        this.isLoading = false
                        this.isSending = false
                    })
            },
            getMessages() {
                if (this.isLoading) return
                if (this.firstLoad) this.showLoadingInfo = true
                if (this.totalMessages < this.offset) return
                this.isLoading = true
                const query = {
                    objectId: this.referral.id,
                    userId:
                        (this.practice && this.practice.practiceType === PracticeTypeEnum.PHYSICIAN) ||
                        this.$auth.isUserPracticeOther()
                            ? this.userId
                            : 0,
                    objectType: 'referral',
                    limit: 10,
                    offset: this.offset,
                    order: 'desc',
                }

                if (this.isChatWithPatient) {
                    query.patientId = Number(this.referral.patient.id)
                }

                if (this.practice && this.practice?.id) {
                    query.practiceId = this.practice?.id
                } else {
                    query.practiceId = 0
                }

                return MessagesAPI.getMessages(query)
                    .then((resp) => {
                        this.totalMessages = resp._headers['x-total-count']
                        let messages = this.messages.reverse()
                        messages.push(...resp)
                        this.messages = messages.reverse()
                        this.firstLoad ? this.scrollToEnd() : (this.messageContainer.scrollTop = 400)
                        this.firstLoad = false
                        this.showLoadingInfo = false
                        this.isLoading = false
                    })
                    .catch(() => {
                        this.firstLoad = false
                        this.showLoadingInfo = false
                        this.isLoading = false
                    })
            },
            scrollToEnd() {
                if (this.messageContainer) {
                    this.messageContainer.scrollTop = this.messageContainer.scrollHeight
                }
            },
            handleScroll() {
                if (this.messageContainer.scrollTop < 40 && !this.isLoading) {
                    !this.isLoading ? (this.offset += this.limit) : null
                    this.getMessages()
                }
            },
            clear() {
                this.firstLoad = true
                this.messages = []
                this.offset = 0
                this.showLoadingInfo = false
                this.isLoading = false
            },

            attachFile() {
                this.$refs.fileInput.click()
            },

            fileSelected(event) {
                this.onFileSelected(event)
                this.$refs.fileInput.value = ''
            },

            removeFile(event) {
                if (!this.isUploading) {
                    this.removeAndValidateFiles(event)
                }
            },
        },
        watch: {
            isOpen(isOpen) {
                if (!isOpen) return
                this.clear()
                this.getMessages().then(() => {
                    this.messageContainer = document.querySelector('#container')
                    document.querySelector('#container').addEventListener('scroll', () => {
                        this.handleScroll()
                    })
                    this.markSeen()
                    this.scrollToEnd()
                })
            },
        },
        mounted() {
            this.practice = this.$auth.getPractice()

            if (this.practice.practiceType === PracticeTypeEnum.PHYSICIAN) {
                this.userId = this.$auth.getActiveMember().id
            } else if (this.$auth.isUserPracticeOther()) {
                this.userId = this.$auth.getUser().id
            } else {
                this.userId = null
            }
        },
    }
</script>
